import { Component } from 'react';
import styles from '../../styles/home/homeSearch.scss';
import cx from 'classnames';
import { Router } from '../../configs/routes';
import { parse } from 'query-string';
import { Api } from '../../api';
import { CDN_CLRSAVE_URL } from '../../constants';
import { VARIANT_VALUES } from '../../constants/variantConstants';
import getConfig from 'next/config';
import {
  algoliaIndex,
  stagingAlgoliaIndex,
} from '../../constants/searchConstants';

const { publicRuntimeConfig } = getConfig();
const api = new Api();

const ENTER_KEY = 13;
const UP_ARROW = 38;
const DOWN_ARROW = 40;

interface Props {
  fullWidth?: boolean;
  placeholder?: string;
  mobileNav?: boolean;
  afterSearch?: () => void;
  handleFundClick?: (e: any) => void;
  hideSearchLabel?: boolean;
  variant?: string;
}

interface State {
  results: any[];
  totalResults: number;
  query: string;
  currentSelectedResult: number;
  placeholder: string;
}

class HomeSearchNew extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { placeholder } = props;
    this.state = {
      results: [],
      totalResults: 0,
      query: '',
      currentSelectedResult: -1,
      placeholder:
        placeholder || 'Find a mutual fund by name, type or fund house',
    };
  }

  public handleKeyDown = (e: any) => {
    const { currentSelectedResult, results } = this.state;
    const { hideSearchLabel } = this.props;
    if (e.keyCode === ENTER_KEY) {
      if (currentSelectedResult !== -1) {
        const selectedItem = results[currentSelectedResult];
        if (selectedItem) {
          const { isinCode, fundMetaData, planType = 'GROWTH' } = selectedItem;
          const { parentSlug, slug } = fundMetaData;
          if (hideSearchLabel) {
            this.props.handleFundClick(`${isinCode}`);
            this.resetState();
          } else {
            Router.pushRoute(
              `/mutual-funds/${parentSlug}/${slug}/${isinCode}?planType=${planType}`
            );
          }
        }
      } else if (!hideSearchLabel) {
        this.navigateToSearch();
      }
    }

    const totalShownItems = results.length;
    const isLastItem = currentSelectedResult === totalShownItems - 1;
    const isFirstItem = currentSelectedResult === 0;
    if (e.keyCode === DOWN_ARROW) {
      this.setState({
        currentSelectedResult: isLastItem ? 0 : currentSelectedResult + 1,
      });
    } else if (e.keyCode === UP_ARROW) {
      this.setState({
        currentSelectedResult: isFirstItem
          ? totalShownItems - 1
          : currentSelectedResult - 1,
      });
    }
  };

  public navigateToFund = (e: any) => {
    const slug = e.target.dataset.slug || e.currentTarget.dataset.slug;
    const productID = e.target.dataset.pid || e.currentTarget.dataset.pid;
    const planType =
      e.target.dataset.plantype || e.currentTarget.dataset.plantype || 'GROWTH';
    let route = `/mutual-funds/${slug}?planType=${planType}`;
    if (productID == null) {
      route = `/mutual-funds/${slug}?type=non_onboarded&planType=${planType}`;
    }
    Router.push(route);
  };

  private handleFundClick = (e: any) => {
    const { handleFundClick } = this.props;
    this.resetState();
    if (handleFundClick) {
      handleFundClick(e);
    } else {
      this.navigateToFund(e);
    }
  };

  private resetState() {
    this.setState({
      query: '',
      results: [],
      totalResults: 0,
      currentSelectedResult: -1,
    });
  }

  public navigateToSearch = () => {
    const { afterSearch } = this.props;
    const filters = JSON.stringify({
      query: this.ref.value,
    });
    const queries = parse(location.search);
    queries.filters = filters;
    if (afterSearch && typeof afterSearch === 'function') {
      afterSearch();
    }
    Router.pushRoute('search', queries);
  };

  public componentDidMount() {
    document.addEventListener('click', this.handleDocumentClick);
    if (window.innerWidth < 1024) {
      this.setState({
        placeholder: 'Find a mutual fund...',
      });
    }
  }

  public componentWillUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  public handleDocumentClick = (e: any) => {
    if (e.target.id !== 'navbar-search') {
      this.setState({
        totalResults: 0,
      });
    }
  };

  public handleChange = (e: any) => {
    const { value } = e.target;
    this.searchFund(value);
  };

  public searchFund = (value?: string) => {
    this.setState({
      query: value,
    });
    if (!value) {
      return;
    }
    const index =
      publicRuntimeConfig.env === 'development' ||
      publicRuntimeConfig.env === 'qa'
        ? stagingAlgoliaIndex
        : algoliaIndex;
    api
      .searchFund(
        value,
        0,
        [['purchaseMode:DIRECT'], ['enabled:true'], [], [], []],
        index
      )
      .then((res) => {
        this.setState({
          results: res.hits.slice(0, 10),
          totalResults: res.nbHits,
        });
      });
  };

  public handleBlur = () => {
    const { query } = this.state;
    this.searchFund(query);
  };

  public handleItemHover = (i: number) => {
    this.setState({
      currentSelectedResult: i,
    });
  };

  public render() {
    const { hideSearchLabel, variant } = this.props;
    const { results, totalResults, query, currentSelectedResult, placeholder } =
      this.state;
    return (
      <div className="lil-relative">
        <input
          placeholder={placeholder}
          id="navbar-search"
          onKeyDown={this.handleKeyDown}
          onChange={this.handleChange}
          onFocus={this.handleBlur}
          value={query}
          className={`lil-w-full lil-input lil-border-sm lil-border-grey-300 lil-mt-5 lil-rounded-xl lil-p-2 lil-pl-12 ${styles['input-placeholder']}`}
          ref={(ref) => {
            this.ref = ref;
          }}
        />
        <span>
          <img
            className={cx(styles['search-icon'], 'lil-absolute ')}
            src="https://assets1.cleartax-cdn.com/cleartax/images/1628142886_search.svg"
            alt="search-btn"
          />
        </span>
        {!hideSearchLabel ? (
          <span
            className={cx(styles['search-help'], 'text--md font--bold', {
              's-btn s-btn--blue': variant === VARIANT_VALUES.B,
            })}
            onClick={this.navigateToSearch}
          >
            Search{' '}
          </span>
        ) : null}

        {totalResults && query ? (
          <div
            className={`lil-rounded-xl lil-bg-white lil-border-grey-300 lil-shadow-sm lil-border-sm lil-py-2 ${styles['autocomplete-results']}`}
          >
            <ul>
              {results.map((result, i) => {
                const {
                  fundName,
                  imageUrl,
                  fundMetaData,
                  isinCode,
                  productId,
                  planType,
                } = result;
                const { parentSlug, slug } = fundMetaData;
                return (
                  <li
                    key={isinCode + '-' + planType}
                    onClick={this.handleFundClick}
                    data-slug={`${parentSlug}/${slug}/${isinCode}`}
                    onMouseEnter={() => this.handleItemHover(i)}
                    data-productid={`${isinCode}`}
                    data-pid={productId}
                    data-plantype={planType}
                    className={`lil-flex lil-items-center lil-flex-wrap lil-pl-6 lil-pr-4 lil-cursor-pointer ${
                      styles['hover-affect']
                    } ${i === currentSelectedResult ? styles['selected'] : ''}`}
                  >
                    <div className="lil-w-1/12 lil-py-2 ">
                      <img
                        src={`${CDN_CLRSAVE_URL}/clearsave${imageUrl}`}
                        width="25"
                        className="lil-rounded-md"
                      />
                    </div>
                    <div
                      className={`lil-w-11/12 lil-pl-2 lil-py-2 ${styles['resultSearch']}`}
                    >
                      <span>{fundName}</span>
                    </div>
                  </li>
                );
              })}
              {!hideSearchLabel && totalResults > 10 ? (
                <li onClick={this.navigateToSearch} className="text--md">
                  <a href="#">Show all results for {query}</a>
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }
}

export default HomeSearchNew;



// WEBPACK FOOTER //
// ./components/home/homeSearchNew.tsx